import { idbKvDel, idbKvGet, idbKvSet } from "../stores/indexedDbStringKv";
import {
  resolvePdfImageSrcMap,
  type ResolvePdfImageOptions,
} from "../utils/pdfImageUtils";
import type { MiContenidoMetricsExportData } from "./miContenidoExportData";
import { isChannelNamedProgram } from "./programPages/channelUmbrellaProgram";
import {
  collectPdfImageUrls,
  resolvePdfImagesToDataUris,
} from "./resolvePdfImagesToDataUris";

/** Cache TTL for dev PDF preview data and resolved images. */
export const METRICS_PREVIEW_CACHE_TTL_MS = 1 * 60 * 1000;

const RAW_CACHE_PREFIX = "dev-metrics-pdf-cache:v27:";
const PREPARED_CACHE_PREFIX = "dev-metrics-pdf-prepared:v27:";

/** Stale preview caches from before channel umbrella pages may omit the channel row. */
export function exportDataMissingChannelProgramPage(
  data: MiContenidoMetricsExportData,
): boolean {
  const pages = data.programPages ?? [];
  if (pages.length <= 1) return false;
  const podcastTitle = data.titles?.podcastTitle?.trim() ?? "";
  const hasChannelPage = pages.some((page) => {
    if (page.isChannelProgram === true) return true;
    const channelName = page.channelName?.trim() || podcastTitle;
    return isChannelNamedProgram(page.name, channelName);
  });
  return !hasChannelPage;
}
const IMAGE_CACHE_PREFIX = "dev-metrics-pdf-img:";

export const isMetricsPreviewLocalEnv =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

export function metricsPreviewCacheKey(
  variant: string,
  entityId: string,
  startDate: string,
  endDate: string,
  rankingStartDate?: string,
  rankingEndDate?: string,
  /** Episode-bearing payloads are a superset; they must not reuse a slim entry. */
  includeEpisodes?: boolean,
): string {
  const ranking =
    rankingStartDate && rankingEndDate
      ? `:rank:${rankingStartDate}:${rankingEndDate}`
      : "";
  const episodes = includeEpisodes ? ":eps" : "";
  return `${variant}:${entityId}:${startDate}:${endDate}${ranking}${episodes}`;
}

function rawStorageKey(cacheKey: string): string {
  return `${RAW_CACHE_PREFIX}${cacheKey}`;
}

function preparedStorageKey(cacheKey: string): string {
  return `${PREPARED_CACHE_PREFIX}${cacheKey}`;
}

function imageStorageKey(url: string): string {
  return `${IMAGE_CACHE_PREFIX}${url}`;
}

type Timestamped<T> = { ts: number; data: T };

function isFresh(ts: number): boolean {
  return Date.now() - ts <= METRICS_PREVIEW_CACHE_TTL_MS;
}

/** Layer A — raw export JSON in localStorage. */
export function readRawExportCache(
  cacheKey: string,
): MiContenidoMetricsExportData | null {
  if (!isMetricsPreviewLocalEnv) return null;
  try {
    const raw = window.localStorage.getItem(rawStorageKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Timestamped<MiContenidoMetricsExportData>;
    if (!isFresh(parsed.ts)) {
      window.localStorage.removeItem(rawStorageKey(cacheKey));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeRawExportCache(
  cacheKey: string,
  data: MiContenidoMetricsExportData,
): void {
  if (!isMetricsPreviewLocalEnv) return;
  try {
    window.localStorage.setItem(
      rawStorageKey(cacheKey),
      JSON.stringify({ ts: Date.now(), data }),
    );
  } catch {
    // Quota errors — cache is best-effort.
  }
}

/** Layer B — session + IndexedDB image map (url → PNG data URI). */
const sessionImageCache = new Map<string, { ts: number; dataUri: string }>();

function readSessionImage(url: string): string | null {
  const entry = sessionImageCache.get(url);
  if (!entry) return null;
  if (!isFresh(entry.ts)) {
    sessionImageCache.delete(url);
    return null;
  }
  return entry.dataUri;
}

function writeSessionImage(url: string, dataUri: string): void {
  sessionImageCache.set(url, { ts: Date.now(), dataUri });
}

async function readIdbImage(url: string): Promise<string | null> {
  if (!isMetricsPreviewLocalEnv) return null;
  try {
    const raw = await idbKvGet(imageStorageKey(url));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Timestamped<string>;
    if (!isFresh(parsed.ts)) {
      void idbKvDel(imageStorageKey(url));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeIdbImage(url: string, dataUri: string): void {
  if (!isMetricsPreviewLocalEnv) return;
  void idbKvSet(
    imageStorageKey(url),
    JSON.stringify({ ts: Date.now(), data: dataUri }),
  ).catch(() => {
    // Best-effort persistence.
  });
}

async function loadCachedImageMap(
  urls: string[],
): Promise<Map<string, string>> {
  const map = new Map<string, string>();

  await Promise.all(
    urls.map(async (url) => {
      const fromSession = readSessionImage(url);
      if (fromSession) {
        map.set(url, fromSession);
        return;
      }
      const fromIdb = await readIdbImage(url);
      if (fromIdb) {
        writeSessionImage(url, fromIdb);
        map.set(url, fromIdb);
      }
    }),
  );

  return map;
}

function persistResolvedImages(map: Map<string, string>): void {
  map.forEach((dataUri, url) => {
    if (dataUri.startsWith("data:")) {
      writeSessionImage(url, dataUri);
      writeIdbImage(url, dataUri);
    }
  });
}

/** Layer C — fully prepared preview payload in IndexedDB. */
export async function readPreparedPreviewCache(
  cacheKey: string,
): Promise<MiContenidoMetricsExportData | null> {
  if (!isMetricsPreviewLocalEnv) return null;
  try {
    const raw = await idbKvGet(preparedStorageKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Timestamped<MiContenidoMetricsExportData>;
    if (!isFresh(parsed.ts)) {
      void idbKvDel(preparedStorageKey(cacheKey));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function writePreparedPreviewCache(
  cacheKey: string,
  data: MiContenidoMetricsExportData,
): void {
  if (!isMetricsPreviewLocalEnv) return;
  void idbKvSet(
    preparedStorageKey(cacheKey),
    JSON.stringify({ ts: Date.now(), data }),
  ).catch(() => {
    // Best-effort — prepared cache may exceed quota on very large payloads.
  });
}

export async function clearPreviewCachesForKey(
  cacheKey: string,
): Promise<void> {
  if (!isMetricsPreviewLocalEnv) return;
  try {
    window.localStorage.removeItem(rawStorageKey(cacheKey));
  } catch {
    // Ignore.
  }
  try {
    await idbKvDel(preparedStorageKey(cacheKey));
  } catch {
    // Ignore.
  }
}

/**
 * Resolve PDF images using session + IDB cache, fetching only cache misses.
 */
export async function resolveImagesWithCache(
  data: MiContenidoMetricsExportData,
  options?: ResolvePdfImageOptions,
): Promise<MiContenidoMetricsExportData> {
  if (!isMetricsPreviewLocalEnv) {
    return resolvePdfImagesToDataUris(data);
  }

  const urls = collectPdfImageUrls(data);
  if (urls.length === 0) return data;

  const cached = await loadCachedImageMap(urls);
  const misses = urls.filter((url) => !cached.has(url));

  let resolved = cached;
  if (misses.length > 0) {
    const fetched = await resolvePdfImageSrcMap(misses, undefined, options);
    resolved = new Map<string, string>([
      ...Array.from(cached.entries()),
      ...Array.from(fetched.entries()),
    ]);
    persistResolvedImages(fetched);
  }

  return resolvePdfImagesToDataUris(data, resolved);
}
